<template>
	<widget-settings-layout ref="layout" v-if="settings" slug="emoteWall" name="Emote Wall" :error="saveError" :saving="saving" :has-unsaved-changes="hasUnsavedChanges" :settings="settings" :saved-settings-json="savedSettingsJson" @save="save">
		<b-card class="mb-3">
			<b-form-row class="mb-3">
				<b-col><b-form-checkbox v-model="settings.physics">Physics simulation</b-form-checkbox></b-col>
				<b-col><b-form-checkbox v-model="settings.physicsHulls" :disabled="!settings.physics">Precise shapes</b-form-checkbox></b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Duration" append="seconds">
						<b-input-group append="seconds">
							<b-input type="number" v-model="settings.duration" min="1" max="120"></b-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Object limit" description="0 = unlimited">
						<b-input type="number" v-model.number="settings.objectLimit" min="0" max="500"></b-input>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Gravity" append="seconds" :disabled="!settings.physics">
						<b-input-group append="N">
							<b-input v-model="settings.gravity" min="0" max="100" step="any"></b-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Emote and emoji size">
						<b-input type="range" v-model="settings.emoteSize" min="1" max="20"></b-input>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-checkbox v-model="settings.showEmojis">Show emojis</b-form-checkbox>
					<b-form-checkbox v-model="settings.memberEmotesOnly">Member emotes only</b-form-checkbox>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Sticker and gift size">
						<b-input type="range" v-model="settings.stickerSize" min="1" max="40"></b-input>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-checkbox v-model="settings.showGifts">Show gifts</b-form-checkbox>
				</b-col>
			</b-form-row>
			<b-form-group label="Extra shapes" description="Input one convex fixed collision shape per line, in the format X1,Y1 X2,Y2 ..." :disabled="!settings.physics">
				<b-textarea v-model="settings.shapes"></b-textarea>
			</b-form-group>
		</b-card>
		<template #save-extra>
			<b-button variant="secondary" class="ml-2" @click="spamTestEmotes">Test emotes</b-button>
		</template>
		<template #preview>
			<emote-wall-widget-preview :settings="settings" :show-shapes="showShapes" :real-emotes="realEmotes" :real-gift-types="realGiftTypes" />
		</template>
		<template #preview-options>
			<b-form-checkbox v-model="showShapes" :disabled="!settings.physics">Show shapes</b-form-checkbox>
		</template>
	</widget-settings-layout>
</template>

<script>
import WidgetSettingsLayout from '@/components/Widget/WidgetSettingsLayout.vue';
import EmoteWallWidgetPreview from '@/components/Widget/EmoteWallWidgetPreview.vue';

export default {
	beforeRouteLeave(to, from, next) {
		if (!this.hasUnsavedChanges) return next();
		this.$refs.layout.showLeaveModal(next, () => this.save());
	},
	data() { return {
		settings: null,
		loading: 0,
		error: null,
		saving: 0,
		saveError: null,
		savedSettingsJson: null,
		// Debug-only preview toggle — not part of settings, never saved (see
		// EmoteWallWidgetPreview's showShapes prop for why).
		showShapes: false,
		realEmotes: [],
		realGiftTypes: [],
	}; },
	components: {
		WidgetSettingsLayout,
		EmoteWallWidgetPreview,
	},
	async mounted() {
		window.addEventListener('beforeunload', this._onBeforeUnload);
		try {
			this.loading++;
			this.error = null;
			const settings = await this.$api.get('widgetSettings/emote-wall');
			this.settings = {
				...settings,
				physics: !!settings.physics,
				physicsHulls: !!settings.physicsHulls,
				showEmojis: !!settings.showEmojis,
				// Renamed from customEmotesOnly — fall back to the old key so an existing
				// choice survives the rename instead of silently resetting to off.
				memberEmotesOnly: settings.memberEmotesOnly ?? !!settings.customEmotesOnly,
				duration: settings.duration || 15,
				emoteSize: settings.emoteSize || 5,
				stickerSize: settings.stickerSize || 5,
				objectLimit: settings.objectLimit ?? 50,
			};
			this.savedSettingsJson = JSON.stringify(this.settings);
		} catch(e) {
			this.error = e;
		} finally {
			this.loading--;
		}
		// Best-effort: the preview just falls back to plain emoji if this fails or comes
		// back empty, so it shouldn't block the settings page over it.
		this.$api.get('emotes/mine').then(emotes => { this.realEmotes = emotes || []; }, () => {});
		// Same catalog app/widgets/dono-media lists — the preview's "spam gifts" and the
		// "Test emotes" button's gift burst both pick icons from this.
		this.$api.get('userGiftTypes', { limit: 1000 }).then(r => { this.realGiftTypes = r?.results || []; }, () => {});
	},
	computed: {
		hasUnsavedChanges() {
			if (this.savedSettingsJson === null) return false;
			return JSON.stringify(this.settings) !== this.savedSettingsJson;
		},
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this._onBeforeUnload);
	},
	methods: {
		_onBeforeUnload(e) {
			if (this.hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = '';
			}
		},
		async save() {
			try {
				this.saveError = null;
				this.saving++;
				await this.$api.post('widgetSettings/emote-wall', this.settings);
				this.savedSettingsJson = JSON.stringify(this.settings);
			} catch(e) {
				console.error('Error saving widget:', e);
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
		// Fire a burst of test chat messages carrying random platform image-emotes (and, if
		// "Show gifts" is on, a few real gift alerts too — playTestAlert's own 'gift' case
		// already picks a random priced type from this user's catalog, the same one
		// app/widgets/dono-media lists, when no explicit giftType is given) down the real
		// alert pipeline, so the live (OBS) widget can be exercised without waiting for
		// live chat. Safe to spam-click. (Intentionally not spawned into the dashboard
		// preview — running a second physics burst there bogs the browser down; the
		// preview gets its own ambient spawn instead, see EmoteWallWidgetPreview.)
		spamTestEmotes() {
			const messages = 8, emotesPerMessage = 4;
			for(let i = 0; i < messages; i++)
				this.$api.post('alerts/test', { type: 'chat', data: { emotes: emotesPerMessage } })
					.catch(e => console.error('test emotes:', e));
			if(this.settings.showGifts) {
				const gifts = 4;
				for(let i = 0; i < gifts; i++)
					this.$api.post('alerts/test', { type: 'gift', data: { threshold: 0 } })
						.catch(e => console.error('test gifts:', e));
			}
		},
	},
};
</script>
